import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import ToggleSwitch from '../ToggleSwitch';
import type { ICustomAlertPreference } from '../../types/api-caller';
import CustomAlertEditor from './CustomAlertEditor';
import { userListsAtom } from '../../store/portfolio';
import type { IUserListRef } from '../../types/company';

interface CustomAlertSummaryCardProps {
  alert: ICustomAlertPreference;
  isEditing: boolean;
  onEdit: (alertId: number) => void;
  onDelete: (alertId: number) => void;
  onToggle: (alertId: number, enabled: boolean) => void;
  onUpdate: (alert: ICustomAlertPreference) => void;
}

function getScopeLabel(alert: ICustomAlertPreference): string {
  if (alert.scope_type === 'PORTFOLIO') return 'Portfolio';
  if (alert.scope_type === 'ALL_LISTS') return 'All Lists';
  if (alert.list_ids && alert.list_ids.length > 0) {
    return `${alert.list_ids.length} selected list${alert.list_ids.length > 1 ? 's' : ''}`;
  }
  return 'Portfolio';
}

const CustomAlertSummaryCard: React.FC<CustomAlertSummaryCardProps> = ({ alert, isEditing, onEdit, onDelete, onToggle, onUpdate }) => {
  const [deleteHovered, setDeleteHovered] = useState(false);
  const [editHovered, setEditHovered] = useState(false);
  const userLists = useRecoilValue<IUserListRef[]>(userListsAtom);

  const selectedListNames = useMemo(() => {
    if (alert.scope_type !== 'SELECTED_LISTS' || !alert.list_ids || alert.list_ids.length === 0) {
      return '';
    }

    const listNameById = new Map(userLists.map((list) => [list.id, list.name]));
    return alert.list_ids.map((listId) => listNameById.get(listId) ?? `List ${listId}`).join(', ');
  }, [alert.scope_type, alert.list_ids, userLists]);

  useEffect(() => {
    if (isEditing) {
      setEditHovered(false);
      setDeleteHovered(false);
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <div className="py-2">
        <CustomAlertEditor alert={alert} mode="edit" onSave={onUpdate} />
      </div>
    );
  }

  return (
    <div className="py-3 cursor-pointer group" onClick={() => alert.alert_id !== undefined && onEdit(alert.alert_id)}>
      <div className="flex flex-row justify-between items-center mb-2">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-black group-hover:text-xl transition-all duration-200">
            {alert.name || 'Unnamed Alert'}
          </h2>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditHovered(false);
              if (alert.alert_id !== undefined) {
                onEdit(alert.alert_id);
              }
            }}
            onMouseEnter={() => setEditHovered(true)}
            onMouseLeave={() => setEditHovered(false)}
          >
            <img src={editHovered ? '/icons/edit-selected.svg' : '/icons/edit.svg'} alt="Edit alert" />
          </button>
          {/* <button
              onClick={(e) => {
                e.stopPropagation();
                if (alert.alert_id !== undefined) {
                  onDelete(alert.alert_id);
                }
              }}
              onMouseEnter={() => setDeleteHovered(true)}
              onMouseLeave={() => setDeleteHovered(false)}
            >
              <img
                src={deleteHovered ? '/icons/remove_selected.svg' : '/icons/remove.svg'}
                alt="Delete alert"
              />
          </button> */}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (alert.alert_id !== undefined) {
                onDelete(alert.alert_id);
              }
            }}
            onMouseEnter={() => setDeleteHovered(true)}
            onMouseLeave={() => setDeleteHovered(false)}
          >
            <img src={deleteHovered ? '/icons/remove_selected.svg' : '/icons/remove.svg'} alt="Delete alert" />
          </button>
          <div onClick={(e) => e.stopPropagation()}>
            <ToggleSwitch
              checked={alert.enabled}
              onChange={(checked) => alert.alert_id !== undefined && onToggle(alert.alert_id, checked)}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-4">
        <div className="flex-1 flex flex-col gap-1 text-sm text-gray-700">
          <div>
            <span className="font-medium">Frequency:</span> {alert.frequency === 'DAILY' ? 'Daily' : 'Weekly'}
          </div>
          <div>
            <span className="font-medium">Increase threshold:</span>{' '}
            {alert.increase_threshold != null ? `${alert.increase_threshold}%` : 'No increase threshold set'}
          </div>
          <div>
            <span className="font-medium">Decrease threshold:</span>{' '}
            {alert.decrease_threshold != null ? `${alert.decrease_threshold}%` : 'No decrease threshold set'}
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-1 text-sm text-gray-700">
          <div>
            <span className="font-medium">Low article exclusion:</span> {alert.exclude_low_articles ? 'Excluded' : 'Included'}
          </div>
          <div>
            <span className="font-medium">Scope:</span> {getScopeLabel(alert)}
          </div>
          {selectedListNames && (
            <div>
              <span className="font-medium">Selected lists:</span> {selectedListNames}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomAlertSummaryCard;
